// Library methods
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Components
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import TableCard from "src/components/shared/tables/TableCard/TableCard";
import { DownloadIcon, ViewFileIcon } from "src/assets/icons";

// Styles
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  getSurfaceTokens,
  noHoverBackgroundSx,
} from "src/styles/mui/muiStylesHelper";

// Hooks
import useOnDemandReportGeneration from "src/hooks/reports/useOnDemandReportGeneration";

// Utils
import { getLocalDate } from "src/utils/date/dateHelper";
import { getTooltipText } from "src/utils/exams/examReportsHelper";

// Types
import { ExamType } from "src/services/exams/types";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Constants
import { DEFAULT_REPORT_TYPE } from "src/constants/enums";

type ExtendedExamType = ExamType & {
  firstName: string;
  lastName: string;
  examName: string;
};

type RecentExamCardType = {
  exam: ExtendedExamType;
};

const RecentExamCard = ({ exam }: RecentExamCardType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const navigate = useNavigate();

  const theme = useTheme();
  const { elevatedSurface } = getSurfaceTokens(theme);
  const smallerThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  const { downloadFile } = useOnDemandReportGeneration({
    patientId: exam?.patient?.id,
    examId: exam?.id,
  });

  const fileType = useMemo(
    () => clinicInfo?.settings?.defaultReportType ?? DEFAULT_REPORT_TYPE,
    [clinicInfo?.settings?.defaultReportType]
  );

  const [isDownloading, setIsDownloading] = useState(false);

  const recentlyCompletedHref = `/patients/${exam?.patient?.id}/exam/${exam?.id}`;

  const completionDate = useMemo(
    () =>
      exam?.completionDate
        ? getLocalDate(exam?.completionDate, clinicInfo?.timezone)
        : "",
    [exam?.completionDate, clinicInfo?.timezone]
  );

  const formattedPatientName = useMemo(
    () => `${exam?.firstName} ${exam?.lastName}`,
    [exam?.firstName, exam?.lastName]
  );

  return (
    <TableCard
      key={exam.id}
      isSelectedForViewing={false}
      isSelectedForDeletion={false}
      onClick={(_) => {}}
      hasHoverState={false}
      variant="dark"
      backgroundColorOverride={
        theme.palette.mode === "dark" ? elevatedSurface : undefined
      }
    >
      <Grid container item xs={12} alignItems="center">
        <Grid container item xs={10} display="flex" alignItems="center">
          <Grid
            item
            xs={12}
            sm={4}
            role="button"
            sx={{
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={(e) => {
              // open page in new tab if the meta key or ctrl key is pressed
              if (e.metaKey || e.ctrlKey) {
                localStorage.setItem("patient_id", exam?.patient?.id);
                window.open("/patients", "_blank");
                return;
              }

              navigate("/patients", {
                state: { patient_id: exam?.patient?.id },
              });
            }}
          >
            <Tooltip
              title={`${exam?.patient?.firstName} ${exam?.patient?.lastName}`}
            >
              <Typography
                sx={{
                  maxWidth: "fit-content",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "14px",
                  paddingRight: "16px",
                }}
              >
                {smallerThanSm && (
                  <span style={{ fontWeight: 600 }}>{t("Patient")}: </span>
                )}
                {formattedPatientName}
              </Typography>
            </Tooltip>
          </Grid>

          <Grid item xs={12} sm={5}>
            <Tooltip
              title={
                <div style={{ whiteSpace: "pre-line" }}>
                  {getTooltipText(exam, t)}
                </div>
              }
            >
              <Typography
                sx={{
                  maxWidth: "fit-content",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "14px",
                  paddingRight: "16px",
                }}
              >
                {smallerThanSm && (
                  <span style={{ fontWeight: 600 }}>{t("Type")}: </span>
                )}
                {exam.examName}
              </Typography>
            </Tooltip>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Tooltip title={completionDate}>
              <Typography
                sx={{
                  maxWidth: "fit-content",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: "14px",
                  paddingRight: "16px",
                }}
              >
                {smallerThanSm && (
                  <span style={{ fontWeight: 600 }}>{t("Completion")}: </span>
                )}
                {completionDate}
              </Typography>
            </Tooltip>
          </Grid>
        </Grid>

        <Grid
          item
          xs={2}
          sx={{
            display: "flex",
            height: "100%",
            justifyContent: "flex-end",
            alignItems: "center",
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Tooltip title={t(`Download {{ fileType }} Report`, { fileType })}>
            <span>
              <IconButton
                onClick={async () => {
                  setIsDownloading(true);
                  await downloadFile();
                  setIsDownloading(false);
                }}
                sx={{
                  py: { xs: "8px", sm: "0px" },
                  color: "inherit",
                  "&.Mui-disabled": {
                    color: "inherit",
                    opacity: "0.5",
                  },
                  ...noHoverBackgroundSx,
                }}
                disabled={isDownloading}
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={t("View Results")}>
            <span>
              <a
                href={recentlyCompletedHref}
                style={{ textDecoration: "none", color: "inherit" }}
                onClick={(e) => {
                  e.preventDefault();

                  // open report in new tab if the meta key or ctrl key is pressed
                  if (e.metaKey || e.ctrlKey)
                    window.open(recentlyCompletedHref, "_blank");
                  // else open the report in the same tab
                  else navigate(recentlyCompletedHref);
                }}
              >
                <IconButton
                  sx={{
                    py: { xs: "8px", sm: "0px" },
                    color: "inherit",
                    ...noHoverBackgroundSx,
                  }}
                >
                  <ViewFileIcon />
                </IconButton>
              </a>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
    </TableCard>
  );
};

export default RecentExamCard;
